import * as Sentry from '@sentry/vue'
import type { App as VueApp } from 'vue'
import type { Router } from 'vue-router'

function parseSampleRate(value: string | undefined): number | undefined {
  if (!value) return undefined
  const rate = Number(value)
  if (Number.isNaN(rate)) return undefined
  return Math.min(Math.max(rate, 0), 1)
}

export function initSentry(app: VueApp, router: Router): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || undefined,
    release: import.meta.env.VITE_SENTRY_RELEASE || __GIT_COMMIT_FULL__,
    dist: __BUILD_TIME__,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.vueIntegration({ app, tracingOptions: { trackComponents: true } }),
    ],
    sendDefaultPii: false,
    initialScope: {
      tags: {
        git_commit: __GIT_COMMIT_FULL__,
        git_branch: __GIT_BRANCH__,
        build_time: __BUILD_TIME__,
      },
    },
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) ?? 0,
  })
}

// Pinia state is otherwise attached to every event verbatim, including the auth JWT sitting in
// the `auth` store and the live WebSocket objects in the `ws` store's session map -- scrub both
// before wiring this in via `pinia.use(sentryPiniaPlugin())`.
export function sentryPiniaPlugin() {
  return Sentry.createSentryPiniaPlugin({
    stateTransformer: (state: Record<string, unknown>) => {
      const safeAuth = { ...((state.auth ?? {}) as Record<string, unknown>) }
      delete safeAuth.token

      const safeWs = { ...((state.ws ?? {}) as Record<string, unknown>) }
      const sessions = safeWs.sessions
      delete safeWs.sessions
      safeWs.sessionCount = sessions instanceof Map ? sessions.size : undefined

      return { ...state, auth: safeAuth, ws: safeWs }
    },
  })
}
